import { CustomDatepicker as DatePicker } from '@components/customDatepicker';
import { isEqual } from '@utils/helpers';
import * as moment from 'moment';
import * as React from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useFragment } from 'react-relay';
import {
    DatePresets,
    datePresets,
    getAllApplyingDatePresets
} from '../../../../widgets/performanceOverview';
import { datepickerFragment } from './Datepicker.relay';
import type { DatepickerPropsType } from './types';

const PerformanceOverviewDatepicker: React.FC<DatepickerPropsType> = (props) => {
    const text = useFragment(datepickerFragment, props.text);

    const datePickerVisTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const rootDomRef = useRef<HTMLDivElement>(null);

    const [startDate, setStartDate] = useState<Date | null>(props.detailXDomain[0]);
    const [endDate, setEndDate] = useState<Date | null>(props.detailXDomain[1]);
    const [availableDatePresets] = useState<Date[]>(datePresets(props.xDomain));
    const [applyingDatePresets, setApplyingDatePresets] = useState<DatePresets[] | null>(
        getAllApplyingDatePresets(props.xDomain, props.detailXDomain[0], props.detailXDomain[1])
    );
    const [visibleStart, setVisibleStart] = useState(false);
    const [visibleEnd, setVisibleEnd] = useState(false);

    useEffect(() => {
        setStartDate(props.detailXDomain[0]);
        setEndDate(props.detailXDomain[1]);
        setApplyingDatePresets(props.applyingDatePresets);
    }, [props.detailXDomain, props.applyingDatePresets]);

    const handleChangeStart = useCallback((changedDate: moment.Moment): void => {
        const presets = getAllApplyingDatePresets(
            props.xDomain, changedDate.toDate(), endDate
        );

        setStartDate(changedDate.toDate());
        setApplyingDatePresets(presets);
    }, [props.xDomain, endDate]);

    const handleChangeEnd = useCallback((changedDate: moment.Moment): void => {
        const newApplyingDatePresets = getAllApplyingDatePresets(
            props.xDomain, startDate, changedDate.toDate()
        );

        setEndDate(changedDate.toDate());
        setApplyingDatePresets(newApplyingDatePresets);
    }, [props.xDomain, startDate]);

    const handlePresetClick = useCallback((index: number): void => {
        const newStartDate = availableDatePresets[index];
        const newApplyingDatePresets = getAllApplyingDatePresets(props.xDomain, newStartDate, props.xDomain[1]);

        setStartDate(newStartDate);
        setEndDate(props.xDomain[1]);
        setApplyingDatePresets(newApplyingDatePresets);
    }, [availableDatePresets, props.xDomain]);

    const handleSave = useCallback((): boolean => {
        const mode = window.getComputedStyle(
            rootDomRef.current, '::before'
        ).getPropertyValue('content').split('"').join('');

        props.updateDetailXDomainHandler([startDate as Date, endDate as Date],
            applyingDatePresets, false);

        const findRoot = (el, sel) => {
            while ((el = el.parentElement)
                && !((
                    el.matches
                    || el.matchesSelector
                    || el.mozMatchesSelector
                    || el.oMatchesSelector
                    || el.msMatchesSelector
                ).call(el, sel))) { } // eslint-disable-line no-empty
            return el;
        };

        if (mode === 'mobile-mode') {
            const parentRoot = findRoot(rootDomRef.current, '.PerformanceOverview');
            parentRoot && window.scrollTo(0, parentRoot.offsetTop);
        }

        return true;
    }, [props.updateDetailXDomainHandler, startDate, endDate, applyingDatePresets]);

    const openDatePicker = useCallback((elementName: string) => {
        if (datePickerVisTimeoutRef.current) {
            clearTimeout(datePickerVisTimeoutRef.current);
            datePickerVisTimeoutRef.current = null;
        }

        if (elementName === 'start') {
            setVisibleStart((prev) => !prev);
            setVisibleEnd(false);
        }
        else if (elementName === 'end') {
            setVisibleStart(false);
            setVisibleEnd((prev) => !prev);
        }
    }, []);

    const closeDatePickerElement = useCallback((elementName: string) => {
        datePickerVisTimeoutRef.current = setTimeout(() => {
            if (elementName === 'start') {
                setVisibleStart(false);
            }
            else if (elementName === 'end') {
                setVisibleEnd(false);
            }
        }, 100);
    }, []);

    const blurDatePicker = useCallback((elementName: string) => {
        let isMobile = false;
        try {
            const docWidth = document.body.clientWidth;
            isMobile = docWidth < 630;
        }
        catch (e) { }

        if (!isMobile) {
            closeDatePickerElement(elementName);
        }
    }, [closeDatePickerElement]);

    const { xDomain } = props;

    const headerCols = typeof text.headercols === 'string' ?
        JSON.parse(text.headercols) : text.headercols;
    const presetDateTexts = typeof text.presetdatetexts === 'string' ?
        JSON.parse(text.presetdatetexts) : text.presetdatetexts;

    const colClasses = [
        'datepicker-preset-header',
        'datepicker-header-start',
        'datepicker-header-end',
    ];

    return (
        <div className={props.datePickerWrapperClassName + ' datePickerWrapper-root'} ref={rootDomRef}>
            <a className='datepicker-cancel' onClick={props.closeDatePicker}>&times;</a>
            <div className='datePickerWrapper-table'>
                <div className='datepickerWrapper-header'>
                    {headerCols.map((textItem, idx) => (
                        <div
                            key={idx}
                            className={`datepickerWrapper-cell ${colClasses[idx]}`}
                        >
                            {idx === 0 ? <span>{textItem}</span> : textItem}
                        </div>
                    ))}
                </div>
                <div className='datepickerWrapper-body'>
                    <div className='datepickerWrapper-row'>
                        <div className='datepickerWrapper-cell datepicker-presets-wrapper'>
                            {presetDateTexts.map((presetText, index) => (
                                <div
                                    key={presetText}
                                    className={
                                        !applyingDatePresets || applyingDatePresets.indexOf(index) === -1 ?
                                            null : 'selected'}
                                    onClick={() => handlePresetClick(index)}
                                >
                                    <span>{presetText}</span>
                                </div>
                            ))}
                        </div>

                        <div className={`datepickerWrapper-cell${visibleStart ? ' is-visible' : ''}`}>
                            <div className={`datepickerWrapper-inline datepickerWrapper-inline-start${visibleStart ? ' is-visible' : ''}`}>
                                <DatePicker
                                    inline
                                    selectsStart
                                    selected={moment(startDate)}
                                    startDate={moment(startDate)}
                                    endDate={moment(endDate)}
                                    minDate={moment(xDomain[0])}
                                    maxDate={moment(endDate).subtract(1, 'days')}
                                    onChange={handleChangeStart}
                                    locale={props.location && props.location.state ? props.location.state.language : 'de'}
                                />
                            </div>
                        </div>

                        <div className={`datepickerWrapper-cell${visibleEnd ? ' is-visible' : ''}`}>
                            <div className={`datepickerWrapper-inline datepickerWrapper-inline-end${visibleEnd ? ' is-visible' : ''}`}>
                                <DatePicker
                                    inline
                                    selected={moment(endDate)}
                                    selectsEnd
                                    startDate={moment(startDate)}
                                    endDate={moment(endDate)}
                                    minDate={moment(startDate).add(1, 'days')}
                                    maxDate={moment(xDomain[1])}
                                    onChange={handleChangeEnd}
                                    locale={props.location && props.location.state ? props.location.state.language : 'de'}
                                />
                            </div>
                        </div>

                        <div className='datepickerWrapper-mobile'>
                            <div>
                                {headerCols[1]}<br />
                                <button
                                    className={`datepickerWrapper-mobileButton${visibleStart ? ' is-active' : ''}`}
                                    onClick={() => openDatePicker('start')}
                                    onBlur={() => blurDatePicker('start')}
                                >
                                    {moment(startDate).format('DD.MM.YYYY')}
                                </button>
                            </div>

                            <div>
                                {headerCols[2]}<br />
                                <button
                                    className={`datepickerWrapper-mobileButton${visibleEnd ? ' is-active' : ''}`}
                                    onClick={() => openDatePicker('end')}
                                    onBlur={() => blurDatePicker('end')}
                                >
                                    {moment(endDate).format('DD.MM.YYYY')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='datepickerWrapper-footer'>
                    <div className='datepickerWrapper-cell' />
                    <div className='datepickerWrapper-cell' />
                    <div className='datepickerWrapper-cell datepicker-savewrapper'>
                        <button onClick={handleSave}>
                            {text.savebuttontext}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const MemoizedDatepicker = memo(PerformanceOverviewDatepicker, (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
});

export { MemoizedDatepicker as Datepicker };
